import { createContext, useContext, useState, useEffect } from 'react';
import { AppIcon as NativeAppIcon } from '@capacitor-community/app-icon';

const isNative = () => !!window.Capacitor?.isNativePlatform?.();

async function setNativeIcon(key) {
  if (!isNative()) return;
  try {
    if (key === 'green') {
      await NativeAppIcon.reset({ suppressNotification: true });
    } else {
      await NativeAppIcon.change({ name: key, suppressNotification: true });
    }
  } catch {}
}

const ICONS = {
  green: {
    label: 'Grøn', bg: '#0d9e72', fg: 'white', accent: '#0d9e72',
    brand: '#0d9e72', brandMuted: 'rgba(13,158,114,0.15)', brandLight: '#4dd8b0',
  },
  blue: {
    label: 'Blå',  bg: '#1a7fd4', fg: 'white', accent: '#1a7fd4',
    brand: '#1a7fd4', brandMuted: 'rgba(26,127,212,0.15)', brandLight: '#60a5fa',
  },
  dark: {
    label: 'Mørk', bg: '#0f1e2e', fg: '#4dd8b0', accent: '#4dd8b0',
    brand: '#4dd8b0', brandMuted: 'rgba(77,216,176,0.15)', brandLight: '#7ce8d0',
  },
};

const AppIconContext = createContext(null);

// Anvend et tema direkte på :root CSS-variabler
function applyTheme(key) {
  const ic = ICONS[key] || ICONS.green;
  const root = document.documentElement;
  root.style.setProperty('--brand',       ic.brand);
  root.style.setProperty('--brand-muted', ic.brandMuted);
  root.style.setProperty('--brand-light', ic.brandLight);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', ic.bg);
}

export function AppIconProvider({ children }) {
  const [iconKey, setIconKey] = useState(() =>
    localStorage.getItem('app_icon') || 'green'
  );

  const icon = ICONS[iconKey] || ICONS.green;

  const setIcon = (key) => {
    if (!ICONS[key]) return;
    localStorage.setItem('app_icon', key);
    setIconKey(key);
    applyTheme(key);
    setNativeIcon(key);
  };

  // Anvend gemt tema ved opstart
  useEffect(() => {
    applyTheme(iconKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppIconContext.Provider value={{ iconKey, icon, icons: ICONS, setIcon }}>
      {children}
    </AppIconContext.Provider>
  );
}

export const useAppIcon = () => {
  const ctx = useContext(AppIconContext);
  if (!ctx) throw new Error('useAppIcon skal bruges inden i AppIconProvider');
  return ctx;
};

// SVG ikon-komponent der renderer det valgte ikon
export function AppIcon({ size = 64, iconKey: keyProp }) {
  const ctx = useContext(AppIconContext);
  const key = keyProp || ctx?.iconKey || 'green';
  const ic = ICONS[key] || ICONS.green;

  return (
    <svg width={size} height={size} viewBox="0 0 1024 1024"
      style={{ borderRadius: size * 0.225, flexShrink: 0 }}>
      <rect width="1024" height="1024" rx="230" fill={ic.bg}/>
      <rect x="262" y="290" width="500" height="380" rx="48" fill="none" stroke={ic.fg} strokeWidth="44"/>
      <line x1="262" y1="460" x2="762" y2="460" stroke={ic.fg} strokeWidth="44"/>
      <circle cx="512" cy="355" r="38" fill={ic.fg}/>
      <rect x="492" y="220" width="40" height="148" rx="20" fill={ic.fg}/>
      <line x1="512" y1="560" x2="512" y2="640" stroke={ic.fg} strokeWidth="36" strokeLinecap="round"/>
      <line x1="472" y1="600" x2="552" y2="600" stroke={ic.fg} strokeWidth="36" strokeLinecap="round"/>
      <line x1="480" y1="568" x2="544" y2="632" stroke={ic.fg} strokeWidth="28" strokeLinecap="round"/>
      <line x1="544" y1="568" x2="480" y2="632" stroke={ic.fg} strokeWidth="28" strokeLinecap="round"/>
    </svg>
  );
}
